/**
 * SankeyFlow — Visual income flow diagram.
 *
 * Pure SVG Sankey chart showing:
 *   Income → Tax | After-Tax → Savings | Expenses | Mortgage | Surplus
 *
 * No d3 dependency — uses custom layout math and cubic bezier curves.
 * Responsive, theme-aware, with hover tooltips.
 */
import { useMemo, useState } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { FF } from '../../utils/format';
import { useApp } from '../../hooks/useApp';
import { buildSankeyData, getNodeColor, monthlyLabel } from './sankeyEngine';

/* ─── Layout constants ─── */
const SVG_W = 680;
const SVG_H = 320;
const PAD_X = 16;
const PAD_Y = 20;
const NODE_W = 14;
const NODE_R = 4;         // border radius
const COL_X = [PAD_X, SVG_W * 0.40, SVG_W - PAD_X - NODE_W]; // 3 column x-positions
const LABEL_GAP = 8;
const NODE_GAP = 10;      // vertical gap between nodes in same column

export default function SankeyFlow() {
  const { s, c, proj } = useApp();
  const [hovered, setHovered] = useState(null);

  // Use the first working year for the flow
  const yearData = useMemo(() => {
    return proj.find((d) => d.a >= s.startAge && d.comp > 0) || proj[0] || {};
  }, [proj, s.startAge]);

  const sankey = useMemo(
    () => buildSankeyData(yearData, s),
    [yearData, s]
  );

  // Layout: calculate y-positions for each node
  const layout = useMemo(() => {
    if (sankey.nodes.length === 0) return { nodes: {}, links: [] };

    const usableH = SVG_H - PAD_Y * 2;
    const columns = [[], [], []];

    // Group nodes by column
    sankey.nodes.forEach((n) => {
      columns[n.column].push({ ...n });
    });

    const nodePositions = {};

    // For each column, distribute nodes vertically proportional to value
    columns.forEach((col, ci) => {
      const totalValue = col.reduce((a, n) => a + n.value, 0);
      const totalGap = Math.max(0, col.length - 1) * NODE_GAP;
      const availableH = usableH - totalGap;

      let y = PAD_Y;
      col.forEach((n) => {
        const h = totalValue > 0
          ? Math.max(8, (n.value / totalValue) * availableH)
          : 20;
        nodePositions[n.id] = {
          ...n,
          x: COL_X[ci],
          y,
          w: NODE_W,
          h,
        };
        y += h + NODE_GAP;
      });

      // Center the column vertically
      const totalUsed = y - NODE_GAP - PAD_Y;
      const offset = (usableH - totalUsed) / 2;
      if (offset > 0) {
        col.forEach((n) => {
          nodePositions[n.id].y += offset;
        });
      }
    });

    // Build link paths with source/target y-tracking for stacking
    const sourceYTracker = {};
    const targetYTracker = {};

    const linkPaths = sankey.links.map((link) => {
      const src = nodePositions[link.source];
      const tgt = nodePositions[link.target];
      if (!src || !tgt) return null;

      // Calculate proportional thickness
      const srcThickness = src.value > 0
        ? (link.value / src.value) * src.h
        : 0;
      const tgtThickness = tgt.value > 0
        ? (link.value / tgt.value) * tgt.h
        : 0;

      // Track stacking within source/target nodes
      const srcStart = sourceYTracker[link.source] || src.y;
      sourceYTracker[link.source] = srcStart + srcThickness;

      const tgtStart = targetYTracker[link.target] || tgt.y;
      targetYTracker[link.target] = tgtStart + tgtThickness;

      return {
        ...link,
        srcX: src.x + src.w,
        srcY0: srcStart,
        srcY1: srcStart + srcThickness,
        tgtX: tgt.x,
        tgtY0: tgtStart,
        tgtY1: tgtStart + tgtThickness,
      };
    }).filter(Boolean);

    return { nodes: nodePositions, links: linkPaths };
  }, [sankey]);

  if (sankey.nodes.length === 0) return null;

  return (
    <Card c={c} delay={0.12}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 16, flexWrap: "wrap", gap: 8,
      }}>
        <Heading ic={null} c={c}>
          <span style={{ fontSize: 17 }}>🌊</span> Cash Flow (Age {yearData.a})
        </Heading>
        <div style={{
          fontSize: 12, color: c.muted, fontWeight: 600,
        }}>
          Annual · {FF(yearData.comp)}/yr gross
        </div>
      </div>

      {/* SVG Sankey */}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          width="100%"
          style={{ maxWidth: SVG_W, display: "block", margin: "0 auto" }}
        >
          {/* Links (flows) */}
          {layout.links.map((link, i) => {
            const color = getNodeColor(link.target, c);
            const isHovered = hovered === link.source || hovered === link.target;
            const midX = (link.srcX + link.tgtX) / 2;

            const d = [
              `M ${link.srcX} ${link.srcY0}`,
              `C ${midX} ${link.srcY0}, ${midX} ${link.tgtY0}, ${link.tgtX} ${link.tgtY0}`,
              `L ${link.tgtX} ${link.tgtY1}`,
              `C ${midX} ${link.tgtY1}, ${midX} ${link.srcY1}, ${link.srcX} ${link.srcY1}`,
              `Z`,
            ].join(" ");

            return (
              <path
                key={`${link.source}-${link.target}`}
                d={d}
                fill={color}
                fillOpacity={isHovered ? 0.25 : 0.10}
                stroke={color}
                strokeOpacity={isHovered ? 0.4 : 0.15}
                strokeWidth={0.5}
                style={{ transition: "fill-opacity 0.2s, stroke-opacity 0.2s" }}
              />
            );
          })}

          {/* Nodes (rectangles) */}
          {Object.values(layout.nodes).map((node) => {
            const color = getNodeColor(node.id, c);
            const isHovered2 = hovered === node.id;

            return (
              <g
                key={node.id}
                onMouseEnter={() => setHovered(node.id)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: "default" }}
              >
                <rect
                  x={node.x}
                  y={node.y}
                  width={node.w}
                  height={node.h}
                  rx={NODE_R}
                  ry={NODE_R}
                  fill={color}
                  fillOpacity={isHovered2 ? 0.9 : 0.7}
                  style={{ transition: "fill-opacity 0.2s" }}
                />

                {/* Label */}
                {node.column === 0 && (
                  <LabelRight
                    x={node.x + node.w + LABEL_GAP}
                    y={node.y + node.h / 2}
                    label={node.label}
                    value={FF(node.value)}
                    monthly={monthlyLabel(node.value)}
                    color={color}
                    c={c}
                  />
                )}
                {node.column === 1 && (
                  <LabelRight
                    x={node.x + node.w + LABEL_GAP}
                    y={node.y + node.h / 2}
                    label={node.label}
                    value={FF(node.value)}
                    monthly={monthlyLabel(node.value)}
                    color={color}
                    c={c}
                  />
                )}
                {node.column === 2 && (
                  <LabelLeft
                    x={node.x - LABEL_GAP}
                    y={node.y + node.h / 2}
                    label={node.label}
                    value={FF(node.value)}
                    monthly={monthlyLabel(node.value)}
                    color={color}
                    c={c}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend strip */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12,
        marginTop: 14, justifyContent: "center",
      }}>
        {sankey.nodes
          .filter((n) => n.column === 1 || n.column === 2)
          .map((n) => (
            <div key={n.id} style={{
              display: "flex", alignItems: "center", gap: 5,
              fontSize: 11, color: c.muted,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: 3,
                background: getNodeColor(n.id, c),
                opacity: 0.7,
              }} />
              {n.label}
              <span style={{
                fontWeight: 600, fontVariantNumeric: "tabular-nums",
                color: getNodeColor(n.id, c),
              }}>
                {Math.round((n.value / sankey.totalIncome) * 100)}%
              </span>
            </div>
          ))
        }
      </div>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   SVG LABEL COMPONENTS
   ═══════════════════════════════════════════════════ */

function LabelRight({ x, y, label, value, monthly, color, c }) {
  return (
    <g>
      <text
        x={x} y={y - 6}
        fill={c.fg}
        fontSize="11"
        fontWeight="600"
        dominantBaseline="auto"
      >
        {label}
      </text>
      <text
        x={x} y={y + 8}
        fill={color}
        fontSize="11"
        fontWeight="700"
        dominantBaseline="auto"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </text>
      <text
        x={x} y={y + 20}
        fill={c.muted}
        fontSize="9"
        fontWeight="500"
        dominantBaseline="auto"
        opacity="0.6"
      >
        {monthly}
      </text>
    </g>
  );
}

function LabelLeft({ x, y, label, value, monthly, color, c }) {
  return (
    <g>
      <text
        x={x} y={y - 6}
        fill={c.fg}
        fontSize="11"
        fontWeight="600"
        textAnchor="end"
        dominantBaseline="auto"
      >
        {label}
      </text>
      <text
        x={x} y={y + 8}
        fill={color}
        fontSize="11"
        fontWeight="700"
        textAnchor="end"
        dominantBaseline="auto"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </text>
      <text
        x={x} y={y + 20}
        fill={c.muted}
        fontSize="9"
        fontWeight="500"
        textAnchor="end"
        dominantBaseline="auto"
        opacity="0.6"
      >
        {monthly}
      </text>
    </g>
  );
}
